// 저작값 기반 오버사이즈 **사전 판정** — "이 구조는 데이터와 무관하게 페이지를 넘치는가".
//
// 성립 근거(결정 `2026-08-18-composer-oversize-item-split.md`): `NodeLayout.height`는 주석
// 이미지에 대해 고정 높이가 아니라 **비율 유지 축소 상한**으로 동작한다(shared
// `report-composer.ts` NodeLayout.height JSDoc, 2026-08-06 확정). 원본이 아무리 커도 그 값을
// 넘지 않으므로, 한 분할 단위의 최악 높이를 **실데이터 없이 저작값만으로** 계산할 수 있다.
// 그래서 샘플 데이터로 그리는 편집 캔버스에서도 실데이터 넘침을 미리 잡는다 — 사후 통지(넘치고
// 나서 PDF가 축소되는 것)로는 저작자가 왜 그렇게 됐는지 모른다.
//
// ⚠️ 계산 원칙은 **"모르는 값은 0"**이다. 글자 수·행 수처럼 데이터에 달린 높이는 세지 않아
// 산출값이 늘 실제 높이의 **하한**이 된다. 하한이 이미 페이지를 넘으면 어떤 데이터로도 넘친다는
// 뜻이라 `certain`이 오탐이 되지 않는다. 반대로 놓치는 경우(하한은 들어가는데 실제로 넘침)는
// 있고, 그건 P6-1의 조건부 재귀 분할과 PDF 비율 유지 축소가 받는다.
//
// 저작 축은 하나도 늘리지 않는다 — 처방은 전부 **기존 높이 필드**(`layout.height`) 조작이다.
//
// DOM 무관 순수 함수다(저작값만 읽는다). 측정치 기반 판정은 `pageMeasure`/`pagePlan` 소관이고,
// 여기서 그 값을 쓰지 않는 것이 이 모듈의 존재 이유다 — 캔버스에 그려지기 전에도 답이 나와야 한다.

import type {
  BoxSpacing,
  ComposerNode,
  NodeLayout,
  ReportTemplate,
} from '@safetyhub/shared';

import { pageContentHeight } from './page';

/**
 * 판정 등급 2종(결정의 "판정 등급 2종" 표).
 *  - `certain`: 이미지에 높이 상한이 있는데도 단위 하한 높이가 이미 페이지 본문을 넘는다.
 *    데이터와 무관하게 **항상** 넘치므로 저작을 고쳐야 한다.
 *  - `possible`: 지금은 들어가지만 이미지 높이 상한이 없어 **원본 크기에 끌려간다**.
 *    처방(상한 지정) 자체가 곧 넘침 방지책이다.
 */
export type OversizeVerdict = 'certain' | 'possible';

/** 넘치는(또는 넘칠 수 있는) 분할 단위 한 건. */
export interface OversizeFinding {
  verdict: OversizeVerdict;
  /** 넘치는 분할 단위를 소유한 노드 id(반복 항목이면 그 repeat 노드 id). */
  nodeId: string;
  /** 단위의 성격 — 반복 항목 1개인가, 최상위 노드 통짜인가. 문구가 갈린다. */
  unit: 'node' | 'repeat-item';
  /** 저작값으로 계산한 단위 높이(px) — 실제 높이의 하한. */
  declaredHeight: number;
  /** 이 양식의 페이지 본문 높이(px). */
  pageContentH: number;
  /** `certain`일 때 넘치는 양(px). */
  overflowBy?: number;
  /** 처방 대상 이미지 노드 id들 — certain=상한이 지정된 것 / possible=상한이 없는 것. */
  imageNodeIds: string[];
  /** `certain`일 때 제안 높이(px) — 가장 큰 이미지를 이 값 이하로 낮추면 들어간다. 0 이하면 생략. */
  suggestedImageHeight?: number;
}

/** 한 서브트리를 훑은 결과 — 높이와 처방 후보 이미지를 **같은 순회**에서 모은다. */
interface Scan {
  /** 저작값 하한 높이(px, 바깥 여백 포함). */
  height: number;
  /** `layout.height`가 숫자인 image — 상한이 있어 "값을 낮춘다"는 처방이 통하는 대상. */
  capped: { nodeId: string; height: number }[];
  /** `layout.height`가 숫자가 아닌 image — 상한이 없어 원본 크기에 끌려간다. */
  uncappedIds: string[];
}

const EMPTY_SCAN = (): Scan => ({ height: 0, capped: [], uncappedIds: [] });

/** 세로 방향 여백 합(top+bottom). 좌우는 높이에 영향이 없다. */
function verticalSpacing(spacing: BoxSpacing | undefined): number {
  return (spacing?.top ?? 0) + (spacing?.bottom ?? 0);
}

/** `layout.height`가 숫자면 그 값 — 고정 박스(또는 이미지 축소 상한)의 표식. */
function fixedHeight(layout: NodeLayout | undefined): number | undefined {
  return typeof layout?.height === 'number' ? layout.height : undefined;
}

/** 자식 목록을 세로 flow로 훑는다 — 높이 합(+ 사이 gap)과 이미지 목록을 함께 모은다. */
function scanFlow(children: ComposerNode[], gap: number): Scan {
  const out = EMPTY_SCAN();
  for (const child of children) {
    const scan = scanNode(child);
    out.height += scan.height;
    out.capped.push(...scan.capped);
    out.uncappedIds.push(...scan.uncappedIds);
  }
  out.height += gap * Math.max(0, children.length - 1);
  return out;
}

/** 자식 목록을 겹침/가로 배치로 훑는다 — 높이는 가장 큰 자식 하나. */
function scanOverlay(children: ComposerNode[]): Scan {
  const out = EMPTY_SCAN();
  for (const child of children) {
    const scan = scanNode(child);
    out.height = Math.max(out.height, scan.height);
    out.capped.push(...scan.capped);
    out.uncappedIds.push(...scan.uncappedIds);
  }
  return out;
}

/**
 * 노드 하나의 저작값 하한 높이 + 그 안의 이미지 목록.
 *
 * 규칙(결정의 "선언 높이 산출"):
 *  1. 공통으로 바깥 세로 여백(`layout.margin`)을 더한다.
 *  2. `layout.height`가 숫자면 그 값이 박스 높이이고 **자식을 순회하지 않는다** — 고정 박스라
 *     안쪽이 아무리 커도 단위 높이가 변하지 않고, 그래서 안쪽 이미지는 처방 대상도 아니다.
 *  3. 아니면 kind별로 컨테이너는 자식에서, leaf는 `minHeight`에서(=모르면 0) 얻는다.
 *  4. 마지막에 `minHeight`를 하한으로 적용한다.
 */
function scanNode(node: ComposerNode): Scan {
  const layout: NodeLayout | undefined = node.layout;
  const fixed = fixedHeight(layout);
  const out = EMPTY_SCAN();

  if (node.kind === 'image') {
    if (fixed === undefined) out.uncappedIds.push(node.id);
    else out.capped.push({ nodeId: node.id, height: fixed });
  }

  let box: number;
  if (fixed !== undefined) {
    box = fixed;
  } else {
    switch (node.kind) {
      case 'group': {
        const inner =
          node.direction === 'row'
            ? scanOverlay(node.children)
            : scanFlow(node.children, node.gap ?? 0);
        out.capped.push(...inner.capped);
        out.uncappedIds.push(...inner.uncappedIds);
        box = verticalSpacing(layout?.padding) + inner.height;
        break;
      }
      case 'stack': {
        const inner = scanOverlay(node.children);
        out.capped.push(...inner.capped);
        out.uncappedIds.push(...inner.uncappedIds);
        box = verticalSpacing(layout?.padding) + inner.height;
        break;
      }
      case 'table': {
        // 행 높이만 센다 — 'auto'/'%'는 내용(데이터)에 달렸으므로 0이다. 같은 이유로 셀 content는
        // 순회하지 않는다(셀 안 이미지 높이는 행 높이에 흡수되지 자기 값으로 단위를 늘리지 않는다).
        const rows = (node.rowHeights ?? []).reduce<number>(
          (sum, h) => sum + (typeof h === 'number' ? h : 0),
          0,
        );
        box = verticalSpacing(layout?.padding) + rows;
        break;
      }
      case 'repeat': {
        // **항목 1개분**만 센다 — 항목 수는 데이터라 판정에 넣을 수 없고, 넣을 필요도 없다.
        // 항목이 N개로 늘어나는 건 P6-1의 항목 단위 분할이 흡수하기 때문이다(한 항목이 페이지를
        // 넘는 것만이 분할로 못 푸는 문제다). columns·gap도 항목이 하나뿐이라 더하지 않는다.
        const inner = scanNode(node.template);
        out.capped.push(...inner.capped);
        out.uncappedIds.push(...inner.uncappedIds);
        box = inner.height;
        break;
      }
      case 'branch': {
        // 슬롯 중 **최소**를 쓴다 — "어느 조건이 켜져도 넘친다"가 성립해야 확정 판정이라 max가
        // 아니다. 처방 대상(capped)도 그 최소 슬롯 것만 싣는다(다른 슬롯 이미지를 낮춰 봐야 이
        // 판정이 안 풀린다). 반대로 상한 없는 이미지(uncapped)는 어느 슬롯이 켜지든 원본에
        // 끌려가므로 전 슬롯을 합친다.
        let best: Scan | undefined;
        for (const slot of node.branches) {
          const scan = scanFlow(slot.children, 0);
          if (!best || scan.height < best.height) best = scan;
          out.uncappedIds.push(...scan.uncappedIds);
        }
        out.capped.push(...(best?.capped ?? []));
        box = best?.height ?? 0;
        break;
      }
      default:
        // leaf(text/image/spacer/line/rect/checkbox/choiceGroup/special) — 콘텐츠 높이는 데이터에
        // 달렸으므로 하한은 minHeight뿐이다(미지정=0).
        box = layout?.minHeight ?? 0;
        break;
    }
  }

  out.height = Math.max(box, layout?.minHeight ?? 0) + verticalSpacing(layout?.margin);
  return out;
}

/**
 * 이 양식에서 **페이지를 넘치는(넘칠 수 있는) 분할 단위**를 전부 찾는다 — 저작값만 읽는 순수 판정.
 *
 * 분할 단위는 `planEditPages`가 다루는 단위와 같다: `body`의 최상위 노드가 repeat이면 **항목
 * 1개**, 아니면 그 노드 자신(통짜). 최상위 group 안의 repeat은 group이 통짜로 움직이므로 group
 * 전체가 한 단위이고, 위 높이 규칙이 자동으로 그렇게 계산한다(별도 분기 없음).
 *
 * 한 단위에서 certain과 possible이 동시에 성립하면 **certain만** 낸다 — 같은 자리에 경고 두 개는
 * 노이즈고, 처방도 certain 쪽이 더 구체적이다.
 */
export function findOversizeUnits(template: ReportTemplate): OversizeFinding[] {
  const page = template.page as ReportTemplate['page'] | undefined;
  // 저장된 JSON에 page가 없는 양식(구 데이터·최소 픽스처)에서 배지 전체가 죽지 않게 한다.
  if (!page) return [];
  const pageContentH = pageContentHeight(page);
  const findings: OversizeFinding[] = [];

  for (const node of template.body ?? []) {
    const scan = scanNode(node);
    const unit = node.kind === 'repeat' ? 'repeat-item' : 'node';

    if (scan.height > pageContentH) {
      const overflowBy = scan.height - pageContentH;
      // 가장 큰 이미지를 넘친 만큼 낮추면 단위가 페이지에 들어간다 — 다른 높이는 그대로 두고
      // 한 곳만 고치면 되는 처방이라, 저작자가 바로 실행할 수 있다.
      const largest = scan.capped.reduce<number>((max, img) => Math.max(max, img.height), 0);
      const suggested = largest - overflowBy;
      findings.push({
        verdict: 'certain',
        nodeId: node.id,
        unit,
        declaredHeight: scan.height,
        pageContentH,
        overflowBy,
        imageNodeIds: scan.capped.map((img) => img.nodeId),
        ...(suggested > 0 ? { suggestedImageHeight: suggested } : {}),
      });
      continue;
    }

    if (scan.uncappedIds.length > 0) {
      findings.push({
        verdict: 'possible',
        nodeId: node.id,
        unit,
        declaredHeight: scan.height,
        pageContentH,
        imageNodeIds: scan.uncappedIds,
      });
    }
  }

  return findings;
}
